<template>

  <ReportModal
    :is-open="showModal"
    :modal-title="modalTitle"
    @modalIsClose="closeModal()"
  >
    <template #content>
      <Preview
        :employee="currentEmployee"
      />
    </template>
    <template #actions>
      <div class="flex justify-between w-full">
        <Create
          :label="'Relatório'"
          :icon="'fa-solid fa-file-invoice'"
          @click="generateReport(currentEmployee.id)"
          />
          <Create
          :label="'Crachá'"
          :icon="'fa-regular fa-id-badge'"
          @click="generateBadges(currentEmployee.id)"
        />
      </div>
    </template>
  </ReportModal>

  <table
    class="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400"
  >
    <thead class="text-xs text-gray-700 uppercase bg-gray-50">
      <tr>
        <slot name="head" />
      </tr>
    </thead>

    <tbody>
      <tr
        v-for="(row, index) in data"
        :key="index"
        class="bg-white border-b hover:bg-gray-50"
      >
        <!-- foto, nome e função -->
        <th
          scope="row"
          class="flex items-center px-6 py-4 text-gray-900 whitespace-nowrap"
        >
          <!-- foto -->
          <img
            class="w-10 h-10 rounded-full"
            :src="
              !row['image']
                ? defaultImg
                : imageServe + row['image']
            "
            :alt="'Foto de ' + row['name']"
          />

          <!-- nome e função -->
          <div class="ps-3">
            <div class="text-base font-semibold">
              {{ row["name"] }}
            </div>

            <div class="font-normal text-gray-500">
              {{ row["role"] }}
            </div>
          </div>
        </th>

        <!-- doc e cell -->
        <td scope="row" class="px-6 py-4 whitespace-nowrap">
          <div>
            <div
              title="CPF"
              class="text-base text-gray-900"
            >
              {{
                row["cpf"]?.length > 20
                  ? 'CPF: ' + row["cpf"].slice(0, 20).concat("...")
                  : 'CPF: ' + row["cpf"]
              }}
            </div>

            <div class="font-normal text-gray-500 lowercase">
              {{
                row["rg"]?.length > 20
                  ? 'RG: ' + row["rg"].slice(0, 20).concat("...")
                  : 'RG: ' + row["rg"] ?? 'não informado'
              }}
            </div>
          </div>
        </td>

        <!-- contato -->
        <td class="px-1 py-4">
          <div class="flex items-center">
            <div class="rounded-full cursor-pointer" title="Chamar no whatsapp">
              <component
                class="w-5 h-5 mt-auto mb-auto mr-1 text-green-500"
                @click="redirectToWhatsApp(row['phone'])"
                :is="PhWhatsappLogo"
              ></component>
            </div>
            {{ row["phone"] }}
          </div>
        </td>

        <!-- contratação -->
        <td class="px-6 py-4">
          <div class="flex items-center">
            {{ formatDate(row["registered"]) }}
          </div>
        </td>

        <!-- ações -->
        <td class="px-6 py-4">
          <div class="flex justify-center font-medium">

            <!-- editar -->
            <router-link
              title="Alterar dados do functionário"
              class="text-yellow-600 mr-1 hover:underline cursor-pointer"
              :to="'/employees/edit/' + row['id']"
            >
              <component :is="PhNotePencil" class="h-6 w-6" />
            </router-link>

            <!-- relatórios -->
            <a
              title="Gerar relatório"
              @click="showReport(row)"
              class="text-green-600 ml-1 hover:underline"
            >
              <component :is="PhFileDoc" class="h-6 w-6" />
            </a>

            <!-- remover -->
            <a
              :title="'Desativar ' + row['name'] + ' da lista'"
              @click="deleteEmployee(row['id'])"
              class="text-red-600 ml-1 hover:underline"
            >
              <component :is="PhTrash" class="h-6 w-6" />
            </a>

          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup>
import ReportModal from '@/components/Modals/Reports.vue'
import Create from '@/components/Buttons/Create.vue'
import Preview from './Report/Preview.vue'
import moment from 'moment'
import { PhFileDoc, PhNotePencil, PhPencilCircle, PhPencilSimple, PhPenNib, PhTrash, PhWhatsappLogo } from '@phosphor-icons/vue'
import employeesService, { PATH } from '../services/employees.service'
import { PdfCreate } from '@/utils/reports/generate-blob'
import { showToast as toast } from '@/helpers/UseToasts'
import { defineProps, ref } from 'vue'
import { storeApp } from '@/store'

const imageServe = process.env.VUE_APP_IMAGE_SERVE + 'storage/'
const defaultImg = require('@/assets/logo/icon.png')
const showModal = ref(false)
const modalTitle = ref('Selecione o relatório que deseja')
const currentEmployee = ref({})

const props = defineProps({
  data: [Object, Array]
})

const emit = defineEmits(['delete'])

function showReport (employeeData) {
  currentEmployee.value = employeeData
  showModal.value = true
}

function closeModal () {
  showModal.value = false
  currentEmployee.value = {}
}

async function generateReport (id) {
  storeApp.state.loading = true

  const url = `${process.env.VUE_APP_API_URL}${PATH}reports/${id}`
  const report = new PdfCreate(url)

  return await report.fetchPdf().finally(() => {
    storeApp.state.loading = false
  })
}

async function generateBadges (id) {
  storeApp.state.loading = true

  const url = `${process.env.VUE_APP_API_URL}${PATH}${id}/badges`
  const report = new PdfCreate(url)

  return await report.fetchPdf().finally(() => {
    storeApp.state.loading = false
  })
}

async function deleteEmployee (id) {
  storeApp.state.loading = true
  const confirm = window.confirm('Deseja realmente desativar esse funcionário ?')
  if (confirm) {
    await employeesService.deleteEmployee(id).then(res => {
      if (res.data.success) {
        toast(
          'Sucesso',
          'Funcionário foi desativado',
          'success'
        )
      }
      window.location.reload()
    }).catch(err => {
      toast(
        'Aviso',
        'Algo deu errado ao desativar funcionário',
        'error'
      )
    })
  }
  storeApp.state.loading = false
}

function formatDate (date) {
  if (date !== null) {
    return moment(date).format('D/MM/Y')
  }

  return 'não informado'
}

// botão do whatsapp
function redirectToWhatsApp (phone) {
  const formattedPhone = phone
    .replace(/\D/g, '')
    .replace(/^0/, '')
    .replace(/^/, '55')

  const whatsappUrl = `https://wa.me/${formattedPhone}`

  if (isIOS()) {
    return location.href = whatsappUrl
  }

  return window.open(whatsappUrl, '_blank')
}

function isIOS () {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

</script>
